import * as React from 'react';
import { css } from '@emotion/core';
import Card from '../../../layout/card/Card';
import CardHeader from '../../../layout/card/CardHeader';
import { useMsgText } from '../../../localization/hooks';
import { Str } from '../../../localization/translationKeys';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { COLORS } from '../../../globalStyle/colors';
import { FLEX_COLUMN, FLEX_ROW } from '../../../layout/flexBox';
import '../../../globalStyle/react-add-to-calendar.css';
import '../../../globalStyle/react-datepicker.min.css';
import DatePicker from 'react-datepicker';
import { BENCHMARKS } from '../../../language/time';
import { onMobileDevice } from '../../../globalStyle/mediaQueries';
import AddToCalendarBtn from '../../../forms/AddToCalendarBtn';
import { useCompletionDate } from './hooks';
import LocalDiv from '../../../localization/LocalDiv';
import Msg from '../../../localization/Msg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import Spacer from '../../../layout/Spacer';
import { useToggle } from '../../../core/hooks/useToggle';
import { useAppState } from '../../../core/redux/hooks';


const styles = {
  root:             [
    FLEX_COLUMN,
    css`
      padding: 15px;
      align-items: stretch;
    `,
  ],
  header:           css`
    margin-bottom: 30px;
  `,
  headerIcon:       css`
    font-size: 20px;
  `,
  date:             css`
    color: ${COLORS.BLUE};
    border: none;
    padding: 10px 0;
    text-align: center;
    width: 100%;
    font-size: 12px;
    
    &:hover { 
      cursor: pointer;
      color: ${COLORS.HOVER_GREY};
    }
  `,
  addToCalBtn:      [
    css`
      margin: 30px 10px 10px;
      
      ${onMobileDevice} {
        margin-top: 0;
      } 
    `,
  ],
  disabled:         css`
    opacity: .4;
  `,
  addDate:          css`
    margin: 0 auto;
    color: ${COLORS.BLUE};

    &:hover { 
      cursor: pointer;
      color: ${COLORS.HOVER_GREY};
    }
  `,
  collaboratorDate: css`
    margin: 35px auto 65px;
  `,
};


type Props = {
  devPlanItemId: number;
  actionId: number;
  completeBy: Date;
  isParticipant: boolean;
  className?: string;
};


/**
 * CompletionCard: a component that shows and updates the Complete By date for an action.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const CompletionCard: React.FC<Props> = props => {
  const title = useMsgText(Str.CompleteBy);

  const { selectedDate, onChangeDate, loading } = useCompletionDate(
    props.devPlanItemId,
    props.actionId,
    props.completeBy,
  );

  const [startOpen, toggleStartOpen] = useToggle(false);

  const initializeDate = () => {
    onChangeDate(new Date());
    toggleStartOpen();
  };
  
  const code = useAppState(state => state.locale.lang.code);

  return (
    <Card css={styles.root} className={props.className}>
      <CardHeader title={title} icon={faCalendar} iconStyle={styles.headerIcon} css={styles.header} />
      {
        props.isParticipant
          ? selectedDate.valueOf() === BENCHMARKS.Y2K.valueOf()
          ? (
            <LocalDiv css={[FLEX_ROW, styles.addDate]} onClick={initializeDate}>
              <Msg str={Str.AddDate} css={FLEX_COLUMN} />
              <Spacer sizePx={5} css={FLEX_COLUMN} />
              <FontAwesomeIcon
                icon={faPen}
                css={[FLEX_COLUMN]}
              />
            </LocalDiv>
          ) : (
            <DatePicker
              selected={selectedDate}
              onChange={onChangeDate}
              dateFormat='MMMM d, yyyy'
              disabled={loading}
              minDate={BENCHMARKS.Y2K}
              css={[styles.date, loading && styles.disabled]}
              startOpen={startOpen}
            />
          ) : (
            <LocalDiv css={[FLEX_ROW, styles.collaboratorDate]}>
              <Msg
                str={selectedDate ? '' : Str.DateNotAdded}
                defaultMsg={selectedDate.toLocaleDateString(code)}
                css={FLEX_COLUMN}
              />
            </LocalDiv>
          )
      }
      {startOpen && toggleStartOpen}
      {
        props.isParticipant && (
          <AddToCalendarBtn selectedDate={selectedDate} css={styles.addToCalBtn} />
        )
      }
    </Card>
  );
};

export default CompletionCard;
